<script setup lang="ts">
import { computed } from 'vue';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'neutral' | 'neutral-outline' | 'neutral-tonal';
export type ButtonSize = 'sm' | 'md' | 'lg';
export type ButtonType = 'button' | 'submit' | 'reset';

interface Props {
  variant?: ButtonVariant;
  size?: ButtonSize;
  type?: ButtonType;
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  ariaLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'md',
  type: 'button',
  disabled: false,
  loading: false,
  fullWidth: false,
  ariaLabel: undefined,
});

defineEmits<{ (event: 'click', payload: MouseEvent): void }>();

const isInert = computed(() => props.disabled || props.loading);

const rootClasses = computed(() => [
  'pj-button',
  `pj-button--${props.variant}`,
  `pj-button--${props.size}`,
  {
    'pj-button--full-width': props.fullWidth,
    'pj-button--loading': props.loading,
  },
]);
</script>

<template>
  <button
    :type="type"
    :class="rootClasses"
    :disabled="isInert"
    :aria-disabled="isInert ? 'true' : undefined"
    :aria-busy="loading ? 'true' : undefined"
    :aria-label="ariaLabel"
  @click="(event: MouseEvent) => $emit('click', event)"
  >
    <span v-if="$slots['icon-leading'] && !loading" class="pj-button__icon">
      <slot name="icon-leading" />
    </span>

    <span v-if="loading" class="pj-button__spinner" aria-hidden="true">
      <svg viewBox="0 0 24 24" width="16" height="16" focusable="false">
        <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-dasharray="44" stroke-dashoffset="22" />
      </svg>
    </span>

    <span class="pj-button__label" :class="{ 'pj-button__label--hidden': loading }">
      <slot />
    </span>

    <span v-if="$slots['icon-trailing'] && !loading" class="pj-button__icon">
      <slot name="icon-trailing" />
    </span>
  </button>
</template>

<style lang="scss" scoped>
.pj-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  gap: var(--pj-button-icon-gap-md);
  border: var(--pj-button-border-width) solid transparent;
  border-radius: var(--pj-button-radius-default);
  font-family: var(--pj-font-family-sans);
  font-weight: var(--pj-button-font-weight);
  line-height: var(--pj-font-line-height-tight);
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  background-color: transparent;
  color: inherit;
  transition:
    background-color var(--pj-motion-duration-fast) var(--pj-motion-easing-standard),
    border-color var(--pj-motion-duration-fast) var(--pj-motion-easing-standard),
    color var(--pj-motion-duration-fast) var(--pj-motion-easing-standard),
    box-shadow var(--pj-motion-duration-fast) var(--pj-motion-easing-standard),
    transform var(--pj-motion-duration-fast) var(--pj-motion-easing-standard);

  /* Invisible padding to ensure 44x44 touch target for `sm` */
  &::after {
    content: '';
    position: absolute;
    inset: -6px;
    pointer-events: none;
  }

  &:focus { outline: none; }

  &:focus-visible {
    box-shadow: 0 0 0 var(--pj-border-width-ring) var(--pj-color-focus-ring);
  }

  &:disabled,
  &[aria-disabled='true'] {
    cursor: not-allowed;
  }
}

/* Sizes */
.pj-button--sm {
  height: var(--pj-button-height-sm);
  padding-inline: var(--pj-button-padding-x-sm);
  font-size: var(--pj-button-font-size-sm);
  gap: var(--pj-button-icon-gap-sm);
}
.pj-button--md {
  height: var(--pj-button-height-md);
  padding-inline: var(--pj-button-padding-x-md);
  font-size: var(--pj-button-font-size-md);
  gap: var(--pj-button-icon-gap-md);
}
.pj-button--lg {
  height: var(--pj-button-height-lg);
  padding-inline: var(--pj-button-padding-x-lg);
  font-size: var(--pj-button-font-size-lg);
  gap: var(--pj-button-icon-gap-lg);
}

.pj-button--full-width { width: 100%; }

/* Variants */
.pj-button--primary {
  background-color: var(--pj-color-action-primary-bg-default);
  color: var(--pj-color-action-primary-text-default);
  border-radius: var(--pj-button-radius-primary);

  &:not(:disabled):not([aria-disabled='true']):hover {
    background-color: var(--pj-color-action-primary-bg-hover);
    box-shadow: var(--pj-shadow-hover);
  }
  &:not(:disabled):not([aria-disabled='true']):active {
    background-color: var(--pj-color-action-primary-bg-pressed);
    box-shadow: var(--pj-shadow-pressed);
    transform: translateY(1px);
  }
  &:disabled,
  &[aria-disabled='true'] {
    background-color: var(--pj-color-action-primary-bg-disabled);
    color: var(--pj-color-action-primary-text-disabled);
  }
}

.pj-button--secondary {
  background-color: var(--pj-color-action-secondary-bg-default);
  color: var(--pj-color-action-secondary-text-default);
  border-color: var(--pj-color-action-secondary-border-default);

  &:not(:disabled):not([aria-disabled='true']):hover {
    background-color: var(--pj-color-action-secondary-bg-hover);
    color: var(--pj-color-action-secondary-text-hover);
    border-color: var(--pj-color-action-secondary-border-hover);
    box-shadow: var(--pj-shadow-hover);
  }
  &:not(:disabled):not([aria-disabled='true']):active {
    background-color: var(--pj-color-action-secondary-bg-pressed);
    color: var(--pj-color-action-secondary-text-pressed);
    border-color: var(--pj-color-action-secondary-border-pressed);
    box-shadow: var(--pj-shadow-pressed);
    transform: translateY(1px);
  }
  &:disabled,
  &[aria-disabled='true'] {
    background-color: var(--pj-color-action-secondary-bg-disabled);
    color: var(--pj-color-action-secondary-text-disabled);
    border-color: var(--pj-color-action-secondary-border-disabled);
  }
}

.pj-button--danger {
  background-color: var(--pj-color-action-danger-bg-default);
  color: var(--pj-color-action-danger-text-default);

  &:not(:disabled):not([aria-disabled='true']):hover {
    background-color: var(--pj-color-action-danger-bg-hover);
    box-shadow: var(--pj-shadow-hover);
  }
  &:not(:disabled):not([aria-disabled='true']):active {
    background-color: var(--pj-color-action-danger-bg-pressed);
    box-shadow: var(--pj-shadow-pressed);
    transform: translateY(1px);
  }
  &:disabled,
  &[aria-disabled='true'] {
    background-color: var(--pj-color-action-danger-bg-disabled);
    color: var(--pj-color-action-danger-text-disabled);
  }
}

.pj-button--ghost {
  background-color: var(--pj-color-action-ghost-bg-default);
  color: var(--pj-color-action-ghost-text-default);

  &:not(:disabled):not([aria-disabled='true']):hover {
    background-color: var(--pj-color-action-ghost-bg-hover);
    color: var(--pj-color-action-ghost-text-hover);
  }
  &:not(:disabled):not([aria-disabled='true']):active {
    background-color: var(--pj-color-action-ghost-bg-pressed);
    color: var(--pj-color-action-ghost-text-pressed);
  }
  &:disabled,
  &[aria-disabled='true'] {
    background-color: var(--pj-color-action-ghost-bg-disabled);
    color: var(--pj-color-action-ghost-text-disabled);
  }
}

.pj-button--neutral {
  background-color: var(--pj-color-action-neutral-bg-default);
  color: var(--pj-color-action-neutral-text-default);
  border-radius: var(--pj-button-radius-primary);

  &:not(:disabled):not([aria-disabled='true']):hover {
    background-color: var(--pj-color-action-neutral-bg-hover);
    box-shadow: var(--pj-shadow-hover);
  }
  &:not(:disabled):not([aria-disabled='true']):active {
    background-color: var(--pj-color-action-neutral-bg-pressed);
    box-shadow: var(--pj-shadow-pressed);
    transform: translateY(1px);
  }
  &:disabled,
  &[aria-disabled='true'] {
    background-color: var(--pj-color-action-neutral-bg-disabled);
    color: var(--pj-color-action-neutral-text-disabled);
  }
}

.pj-button--neutral-outline {
  background-color: var(--pj-color-brand-white);
  color: var(--pj-color-action-neutral-text-on-light);
  border-color: var(--pj-color-action-neutral-border-default);
  border-radius: var(--pj-button-radius-primary);

  &:not(:disabled):not([aria-disabled='true']):hover {
    border-color: var(--pj-color-action-neutral-border-hover);
    box-shadow: var(--pj-shadow-hover);
  }
  &:not(:disabled):not([aria-disabled='true']):active {
    border-color: var(--pj-color-action-neutral-border-pressed);
    box-shadow: var(--pj-shadow-pressed);
    transform: translateY(1px);
  }
  &:disabled,
  &[aria-disabled='true'] {
    background-color: var(--pj-color-action-neutral-bg-disabled);
    border-color: var(--pj-color-action-neutral-border-disabled);
    color: var(--pj-color-action-neutral-text-on-light);
  }
}

/* Light-gray pill (Figma "Button new") — tonal neutral with trailing accent icon */
.pj-button--neutral-tonal {
  background-color: var(--pj-color-action-neutral-tonal-bg-default);
  color: var(--pj-color-action-neutral-tonal-text-default);
  border-radius: var(--pj-radius-full);

  &:not(:disabled):not([aria-disabled='true']):hover {
    background-color: var(--pj-color-action-neutral-tonal-bg-hover);
    box-shadow: var(--pj-shadow-hover);
  }
  &:not(:disabled):not([aria-disabled='true']):active {
    background-color: var(--pj-color-action-neutral-tonal-bg-pressed);
    box-shadow: var(--pj-shadow-pressed);
    transform: translateY(1px);
  }
  &:disabled,
  &[aria-disabled='true'] {
    background-color: var(--pj-color-action-neutral-tonal-bg-disabled);
    color: var(--pj-color-action-neutral-tonal-text-disabled);
  }
}

/* Loading: layout-stable spinner overlay */
.pj-button__spinner {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: currentColor;

  svg { animation: pj-button-spin 800ms linear infinite; }
}

.pj-button__label { display: inline-block; color: inherit; }
.pj-button__label--hidden { visibility: hidden; }

.pj-button__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

@keyframes pj-button-spin {
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .pj-button { transition: none; transform: none !important; }
  .pj-button__spinner svg { animation: none; }
}
</style>
